import React, { FC, useState, useCallback, useLayoutEffect } from 'react';
import dynamic from 'next/dynamic';
import clsx from 'clsx';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { CinemographHeroProps, CinemographHeroModel } from './CinemographHero.types';
import { StyledCinemographHero } from './CinemographHero.styles';
import { Image } from '@marriott/mi-ui-library';
import { debounce, useDCADynamicModelAlloy } from '../../modules/helpers';

interface PlayerInstance {
  paused: () => boolean;
  pause: () => void;
  play: () => void;
}

const ReactPlayerLoader = dynamic(() => import('@brightcove/react-player-loader'), {
  ssr: false,
});

export const CinemographHero: FC<CinemographHeroProps> = props => {
  const [player, setPlayer] = useState<PlayerInstance | null>(null);
  const [viewPort, setViewPort] = useState('');

  const [videoLoading, setVideoLoading] = useState<boolean>(true);

  /**
   * runs before the browser paints the screen....
   */
  useLayoutEffect(() => {
    /**
     * debouncing handle resize event as it trigger's on every subsequent re size.
     * function will be executed only after the user has stopped resizing for a 300ms delay
     */
    const debouncedResize = debounce(handleResize, 300);

    window.addEventListener('resize', debouncedResize);
    handleResize();
    return () => {
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);

  const handleResize = useCallback(() => {
    const xs = window.matchMedia('(max-width: 767px)');
    if (xs?.matches) {
      setViewPort('Mobile');
    } else {
      setViewPort('viewPortM');
    }
  }, []);

  /**
   * Fetching the target response
   */
  const mboxParam = props?.model?.mboxParameter;

  /***
   * Makes the call to target if mboxParam is authored
   */
  const targetResponse = useDCADynamicModelAlloy(mboxParam);
  const targetData = targetResponse?.targetData;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const attachAnalyticsAttr = (success: any) => {
    const { ref } = success;
    if (!ref) return;
    ref.controls_ = false;
    setPlayer(ref);
    setTimeout(() => {
      setVideoLoading(false);
    }, 300);
  };

  const handleClickEvent = () => {
    if (player) {
      if (player.paused()) {
        player.play();
        setVideoPlaying(true);
      } else {
        player.pause();
        setVideoPlaying(false);
      }
    }
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handlePlayPause = (event: any) => {
    if (event.key === 'Enter' || event.type === 'click' || event.keyCode === 13) {
      if (player) {
        if (player.paused()) {
          player.play();
          setVideoPlaying(true);
        } else {
          player.pause();
          setVideoPlaying(false);
        }
      }
    }
  };
  const options = {
    autoplay: true,
    muted: true,
    aspectRatio: '16:9',
    loop: true,
    preload: 'auto',
    src: 'https://players.brightcove.net',
  };
  /**
   * Replacing the AEM model data with target data if target Response is present
   */
  const Model: CinemographHeroModel = targetData ? targetData : props?.model;
  const playLabel = Model?.playLabel;
  const pauseLabel = Model?.pauseLabel;
  const [videoPlaying, setVideoPlaying] = useState<boolean>(options?.autoplay);

  return (
    <StyledCinemographHero className="m-container-fullbleed">
      <div
        className={clsx(
          Model?.assetProperties?.videoAccountId
            ? `loader-cinemograph loader-cinemograph-${Model?.assetProperties?.variation} color-scheme2`
            : '',
          Model?.dynamicImage ? 'loader-cinemograph loader-cinemograph-image' : ''
        )}
      >
        <span
          className={clsx(
            'brand-logo-luxury-Mark cinemograph-loading-logo',
            videoLoading ? 'noanimation' : 'animate-logo',
            Model?.assetProperties?.videoAccountId ? '' : 'd-none'
          )}
        ></span>
        <span
          className={clsx(videoLoading ? 'sr-only' : 'd-none', Model?.assetProperties?.videoAccountId ? '' : 'd-none')}
        >
          brand-logo-luxury-Mark
        </span>
        {Model?.activateOverlay && (
          <span className="row overlay_text p-1">
            <span className="icon-location t-label-xs mr-1" />
            <a href={Model?.overlayPath} className="t-label-xs">
              {Model?.overlayTitle}
            </a>
          </span>
        )}
        {Model?.dynamicImage && viewPort !== '' && (
          <Image
            title=""
            altText={Model?.dynamicImage?.altText}
            defaultImageURL={Model?.dynamicImage?.assetPath}
            renditions={
              viewPort !== 'Mobile'
                ? Model?.dynamicImage?.renditions
                : Model?.dynamicMobileImage?.renditions
                ? Model?.dynamicMobileImage?.renditions
                : Model?.dynamicImage?.renditions
            }
            customClass={clsx('cinemograph-image')}
            loading="eager"
          />
        )}

        {!Model?.dynamicImage && Model?.assetProperties?.videoAccountId && viewPort !== '' && (
          <div
            className={clsx(
              'video-container',
              Model?.overlapNavigation && 'video-overlay',
              `cinemograph-${Model?.assetProperties?.variation}`
            )}
          >
            <ReactPlayerLoader
              id={`luxury-cinemograph-${Model?.assetProperties?.videoAccountId}`}
              accountId={Model?.assetProperties?.videoAccountId as string}
              options={options}
              videoId={
                viewPort !== 'Mobile'
                  ? (Model?.assetProperties?.brightcoveVideoId as string)
                  : ((Model?.assetProperties?.brightcoveMobileVideoId
                      ? Model?.assetProperties?.brightcoveMobileVideoId
                      : Model?.assetProperties?.brightcoveVideoId) as string)
              }
              playerId={Model?.assetProperties?.playerId ?? 'default'}
              poster={Model?.assetProperties?.videoThumbnail ?? ''}
              onSuccess={attachAnalyticsAttr}
            />
            <div
              className={clsx(
                'icon-play-pause custom_click_track ',
                Model?.typeOfVideoControl === 'dark-theme' && 'dark-video-control'
              )}
              onKeyDown={e => handlePlayPause(e)}
              onClick={() => {
                handleClickEvent();
              }}
              tabIndex={0}
              custom_click_track_value={`${Model?.assetProperties?.assetType} | ${
                videoPlaying ? pauseLabel : playLabel
              } button | internal`}
              role="button"
            >
              {!videoLoading && !videoPlaying && (
                <span>
                  <span className="icon-l icon-play"></span>
                  <span className="sr-only">{playLabel}</span>
                </span>
              )}
              {!videoLoading && videoPlaying && (
                <span>
                  <span className="icon-l icon-pause"></span>
                  <span className="sr-only">{pauseLabel}</span>
                </span>
              )}
            </div>
            {Model?.applyScrim && <div className="cinemograph--scrim-component"></div>}
          </div>
        )}
      </div>
    </StyledCinemographHero>
  );
};

export const CinemographHeroConfig = {
  emptyLabel: 'CinemographHero',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/cinemograph-hero/v1/cinemograph-hero`,
};
//TODO:Need to fix the lint issue for editable component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const CinemographHeroEditable = (props: any) => (
  <div data-testid="luxury-CinemographHero" data-component-name="m-luxury-CinemographHero">
    <EditableComponent config={CinemographHeroConfig} {...props}>
      <CinemographHero {...props} />
    </EditableComponent>
  </div>
);
